import styled, { css } from "styled-components";
import { mediaMax } from "components/designV2/breakpoints";
import { Radius, Spacing } from "components/designV2/constants";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";

export const ApplicantContentGrid = styled.div<{ $panel?: boolean }>`
  display: grid;
  gap: ${Spacing.xl}px;
  grid-template-columns: 280px minmax(0, 1fr);
  min-height: 0;

  ${(props) =>
    props.$panel &&
    css`
      display: flex;
      flex-direction: column;
      gap: ${Spacing.lg}px;
    `}

  ${mediaMax.tablet} {
    grid-template-columns: 1fr;
  }
`;

export const ApplicantProfileColumn = styled.div<{ $panel?: boolean }>`
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
  min-width: 0;
  position: sticky;
  top: 0;

  ${mediaMax.tablet} {
    position: static;
  }

  ${(props) =>
    props.$panel &&
    css`
      position: static;
    `}
`;

export const ApplicantProfilePanel = styled.section<{ $panel?: boolean }>`
  align-items: center;
  background: ${ColorV2.Blue7};
  border-radius: ${Radius.xl}px;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
  padding: ${Spacing.xl}px;

  ${(props) =>
    props.$panel &&
    css`
      align-items: flex-start;
      background: transparent;
      border-radius: 0;
      flex-direction: row;
      padding: 0;
    `}
`;

export const ApplicantProfileAvatar = styled.div<{ $panel?: boolean }>`
  align-items: center;
  background: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue6};
  border-radius: 999px;
  display: flex;
  height: 144px;
  justify-content: center;
  min-height: 144px;
  min-width: 144px;
  overflow: hidden;
  width: 144px;

  ${(props) =>
    props.$panel &&
    css`
      flex-shrink: 0;
      height: 64px;
      min-height: 64px;
      min-width: 64px;
      width: 64px;
    `}
`;

export const ApplicantProfileImage = styled.img`
  border-radius: 999px;
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

export const ApplicantProfileIdentity = styled.div<{ $panel?: boolean }>`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
  min-width: 0;

  ${(props) =>
    props.$panel &&
    css`
      align-items: flex-start;
      gap: ${Spacing.xs}px;
      padding-top: ${Spacing.xs}px;
    `}
`;

export const ApplicantProfileName = styled.h4<{ $panel?: boolean }>`
  ${TypographyV2.h5};
  color: ${ColorV2.Blue1};
  margin: 0;
  text-align: center;

  ${(props) =>
    props.$panel &&
    css`
      ${TypographyV2.h3};
      text-align: left;
    `}
`;

export const ApplicantProfileTag = styled.span`
  ${TypographyV2.s1};
  background: ${ColorV2.Blue6};
  border-radius: ${Radius.lg}px;
  color: ${ColorV2.Blue3};
  padding: ${Spacing.xs}px ${Spacing.sm}px;
`;

export const ApplicantProfileTags = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs}px;
`;

export const ProfileMeta = styled.span`
  ${TypographyV2.b2};
  color: ${ColorV2.Blue3};
`;

export const StatGrid = styled.div`
  display: grid;
  gap: ${Spacing.sm}px;
  grid-template-columns: 1fr;
  width: 100%;
`;

export const StatCard = styled.div`
  align-items: center;
  background: ${ColorV2.Blue7};
  border-radius: ${Radius.xl}px;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs}px;
  justify-content: center;
  min-height: 88px;
  padding: ${Spacing.md}px;
  text-align: center;
`;

export const StatValue = styled.div`
  ${TypographyV2.b1};
  align-items: center;
  color: ${ColorV2.Blue1};
  display: flex;
  gap: ${Spacing.xs}px;
`;

export const ApplicantSection = styled.section`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
  min-width: 0;
`;

export const ApplicantPanelSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  min-width: 0;
`;

export const ContactList = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs}px;
  max-width: 100%;
  width: 100%;
`;

export const ContactLink = styled.a`
  ${TypographyV2.s1};
  color: ${ColorV2.Blue1};
  max-width: 100%;
  overflow-wrap: anywhere;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const ApplicantSummaryGrid = styled.div`
  display: grid;
  gap: ${Spacing.md}px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  ${mediaMax.mobile} {
    grid-template-columns: 1fr;
  }
`;

export const ApplicantSummaryItem = styled.div`
  background: ${ColorV2.Blue7};
  border-radius: ${Radius.xl}px;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs}px;
  padding: ${Spacing.lg}px;
`;

export const ApplicantPanelCard = styled.section`
  background: ${ColorV2.White};
  border-radius: ${Radius.xl}px;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
  min-width: 0;
  padding: ${Spacing.lg}px;
`;

export const ApplicantPanelHeading = styled.h3`
  ${TypographyV2.h5};
  color: ${ColorV2.Blue1};
  margin: 0;
`;

export const ApplicantInfoGrid = styled.div`
  display: grid;
  gap: ${Spacing.lg}px ${Spacing.xl}px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  ${mediaMax.mobile} {
    grid-template-columns: 1fr;
  }
`;

export const ApplicantInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs}px;
  min-width: 0;
`;

export const ApplicantPanelText = styled.div`
  overflow-wrap: anywhere;
`;

export const SkillsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  max-height: 190px;
  overflow-y: auto;
  padding-right: ${Spacing.xs}px;
`;

export const SkillItem = styled.div`
  align-items: center;
  background: ${ColorV2.White};
  border-radius: ${Radius.lg}px;
  display: flex;
  gap: ${Spacing.md}px;
  justify-content: space-between;
  padding: ${Spacing.sm}px ${Spacing.md}px;
`;

export const SkillCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs}px;
  min-width: 0;
`;

export const EndorsementList = styled.div`
  background: ${ColorV2.Blue7};
  border-radius: ${Radius.lg}px;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  max-height: 180px;
  min-height: 48px;
  overflow-y: auto;
  padding: ${Spacing.md}px;
`;

export const ApplicantVideoWrapper = styled.div`
  aspect-ratio: 16 / 9;
  max-width: 480px;
  width: 100%;
`;

export const ApplicantVideo = styled.video`
  border-radius: ${Radius.lg}px;
  height: 100%;
  object-fit: contain;
  width: 100%;
`;

export const FooterActions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.md}px;
  justify-content: flex-end;
  width: 100%;
`;

export const ApplicantStatusBadge = styled.span<{
  $backgroundColor?: ColorV2;
  $textColor?: ColorV2;
}>`
  ${TypographyV2.b1};
  background: ${(props) => props.$backgroundColor ?? ColorV2.Blue7};
  border-radius: 999px;
  color: ${(props) => props.$textColor ?? ColorV2.Blue2};
  padding: ${Spacing.sm}px ${Spacing.md}px;
`;
